import { isValidElement } from 'react';

/**
 * DS Chip — selectable toggle button (Primary selected / Secondary idle).
 * Uses `aria-pressed` (toggle button pattern) — no `role="group"` wrapper required.
 * Figma: Design-System AIMS-OS node 5048:61477.
 *
 * **Secondary** (idle):   white bg, 2px `#5C5C5C` border, `#2A2A2A` text.
 * **Primary** (selected): `#2173FF` bg, 2px transparent border, white text.
 *
 * Focus changes the border color in-place (no external ring), matching DS:
 * - Primary focus → border becomes `#80AFFF`
 * - Secondary focus → border becomes `#BABABA`
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.children — Chip label
 * @param {boolean} [props.selected=false]
 * @param {'s'|'m'} [props.size='s'] — S = 20px, M = 28px
 * @param {() => void} [props.onClick]
 * @param {boolean} [props.disabled=false]
 * @param {import('react').ElementType|import('react').ReactElement} [props.leftIcon] — Lucide icon component or rendered element
 * @param {string} [props.className]
 */

const SIZE_CLASS = {
  s: 'h-5 gap-1 px-2 text-xs',
  m: 'h-7 gap-1 px-3 text-sm',
};

const ICON_SIZE_CLASS = {
  s: 'h-3 w-3',
  m: 'h-4 w-4',
};

/**
 * Use `border-2 border-transparent` as the default so the chip never shifts size
 * when focus changes the border color (avoids 1px → 2px layout jump).
 */
const PRIMARY_CLASS = [
  'border-2 border-transparent bg-[#2173FF] text-white',
  'hover:bg-[#002F80]',
  'focus-visible:border-[#80AFFF]',
  'dark:bg-[#2173FF] dark:hover:bg-[#04245C] dark:focus-visible:border-[#80AFFF]',
].join(' ');

const SECONDARY_CLASS = [
  'border-2 border-[#5C5C5C] bg-surface text-[#2A2A2A]',
  'hover:bg-surface-hover-subtle',
  'focus-visible:border-[#BABABA]',
  'dark:border-gray-400 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800 dark:focus-visible:border-gray-500',
].join(' ');

const BASE_CLASS = [
  'inline-flex items-center justify-center rounded-full font-medium leading-none',
  'transition-colors duration-150 whitespace-nowrap select-none',
  'focus-visible:outline-none',
].join(' ');

const SelectableChip = ({
  children,
  selected = false,
  size = 's',
  onClick,
  disabled = false,
  leftIcon: LeftIcon,
  className = '',
}) => {
  const resolvedSize = size === 'm' ? 'm' : 's';

  const stateClass = selected ? PRIMARY_CLASS : SECONDARY_CLASS;

  const chipClass = [
    BASE_CLASS,
    SIZE_CLASS[resolvedSize],
    stateClass,
    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  let iconEl = null;
  if (LeftIcon) {
    if (isValidElement(LeftIcon)) {
      iconEl = LeftIcon;
    } else {
      const Icon = LeftIcon;
      iconEl = <Icon className={ICON_SIZE_CLASS[resolvedSize]} aria-hidden />;
    }
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      className={chipClass}
    >
      {iconEl}
      {children}
    </button>
  );
};

export default SelectableChip;
